import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-message-bubble>` says about itself.
 *
 * Three things about it are not obvious from its inputs.
 *
 * `grouped` is decided by whatever draws the list, not by the bubble. The question is "does this
 * continue the message above it", and the message above is something this element cannot see. The
 * rule is `continuesPrevious()` in the library's shared conversation code; `<caos-message-stream>`
 * applies it for you, and a surface drawing bubbles by hand has to apply it itself or every message
 * repeats the header.
 *
 * `body` is markdown, and a small one — the marks people actually type in a message. It is drawn as
 * nodes rather than as HTML, so markup typed into a message is text and a link's address has to
 * survive the same check the platform's rich Text kind applies.
 *
 * A long message is FOLDED, with a control to open it, at a paragraph boundary near the limit. That
 * is a property of the bubble and not of the stream, because the fold has to know where the prose
 * breaks.
 */
const docs: ComponentDocs = {
  aliases: ['chat message', 'message row', 'comment', 'chat bubble', 'post', 'conversation item'],

  answers: [
    'draw one message in a conversation',
    'show who said something and when',
    'render a message body that contains markdown',
    'group consecutive messages from the same person',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'author',
      attribute: 'author',
      type: 'text',
      description:
        'Who said it. Also the source of the initials in the avatar when no avatar text is given — ' +
        'the first letter of each of the first two words.',
    },
    {
      name: 'at',
      attribute: 'at',
      type: 'text',
      description:
        'When it was said, as an ISO-8601 instant. Drawn as a time of day in the reader’s own locale, ' +
        'because the day is said by the divider above rather than on every message. A value the ' +
        'runtime cannot read as a date draws no time rather than drawing a wrong one.',
    },
    {
      name: 'body',
      attribute: 'body',
      type: 'text',
      description:
        'What they said, as markdown. Settable as a property too, and the property wins — a message ' +
        'carries newlines and can run long, and an attribute holding one goes through the browser’s ' +
        'own attribute rules before this component sees it.',
    },
    {
      name: 'avatar',
      attribute: 'avatar',
      type: 'text',
      description:
        'What the avatar shows, when the initials taken from the author are not what you want. One or ' +
        'two characters; it is decorative and is hidden from screen readers, because the author’s ' +
        'name is already read out beside it.',
    },
    {
      name: 'grouped',
      attribute: 'grouped',
      type: 'boolean',
      default: false,
      description:
        'This message continues the one above it: the avatar, name and time are not drawn, and only ' +
        'the body appears. Decided by whatever draws the list — see the notes above.',
    },
    {
      name: 'highlighted',
      attribute: 'highlighted',
      type: 'boolean',
      default: false,
      description:
        'Mark this as the message that was navigated to — from a search result, or a link into the ' +
        'conversation. Held by the attribute rather than by an animation that ends, so a reader who ' +
        'looked away can still see which one it was.',
    },
  ],

  // It reports nothing. A message is a thing that was said; everything you can DO about one belongs
  // to whatever put it there, and arrives through the footer slot.
  events: [],
  state: [],

  slots: [
    {
      name: 'footer',
      takes:
        'Anything offered ABOUT this message — a reaction, a reply count, an action a surface knows how ' +
        'to run. Deliberately a slot and not a set of inputs: what can be done about a message is the ' +
        'application’s vocabulary, not the library’s, and a component that shipped its own action ' +
        'schema would be shipping one application’s.',
    },
  ],

  tokens: [
    { token: '--caos-color-text', controls: 'The message body and the author’s name.' },
    { token: '--caos-color-text-subtle', controls: 'The time beside the name.' },
    { token: '--caos-color-text-muted', controls: 'The initials in the avatar.' },
    { token: '--caos-color-surface-2', controls: 'The avatar behind the initials, the tint on a highlighted message, and behind a mention.' },
    { token: '--caos-color-border', controls: 'The avatar’s edge, and the rules in a table or a code block.' },
    { token: '--caos-color-accent', controls: 'A link in the body, a mention, and the control that opens a folded message.' },
    { token: '--caos-font', controls: 'The typeface the message is set in.' },
    { token: '--caos-font-mono', controls: 'Code, inline and in a block.' },
    { token: '--caos-text-body', controls: 'The size of the message body.' },
    { token: '--caos-text-label', controls: 'The time, the initials and the fold control.' },
    { token: '--caos-font-weight-label', controls: 'The weight of the author’s name, and of a mention in the body.' },
    { token: '--caos-space-1', controls: 'The space above and below one message.' },
    { token: '--caos-space-2', controls: 'The gap between the avatar and the message.' },
    { token: '--caos-space-3', controls: 'The inset from the edge of the conversation.' },
    { token: '--caos-space-4', controls: 'The indent of a list in the body.' },
    { token: '--caos-radius-pill', controls: 'The roundness of the avatar.' },
    { token: '--caos-radius-sm', controls: 'The roundness of a code block, an inline code pill and a mention.' },
    { token: '--caos-focus-ring', controls: 'The ring on the fold control and on a link in the body.' },
  ],

  parts: [
    { part: 'message', is: 'the whole row' },
    { part: 'avatar', is: 'the initials disc' },
    { part: 'header', is: 'the name and time line' },
    { part: 'author', is: 'the name' },
    { part: 'time', is: 'the time' },
    { part: 'body', is: 'the rendered message' },
    { part: 'footer', is: 'whatever was slotted under the message' },
  ],

  methods: [],

  placing:
    'In a message stream, one per message, in the order they were said. It draws nothing around ' +
    'itself — no divider, no unread line, no separator — because those are statements about the ' +
    'sequence and belong to whatever owns it.',

  whenToUse:
    'One thing somebody said, in a conversation with other things somebody said. A single message ' +
    'standing alone with nothing before or after it is a piece of text, and wants whatever the ' +
    'surrounding page uses for text.',

  insteadUse: [
    {
      name: 'KeyValueRow',
      when: 'The subject is a field and its value rather than a person and what they said.',
    },
    {
      name: 'NoticeBanner',
      when:
        'The platform is telling the reader something, rather than a person telling another person. ' +
        'A message has an author; a notice does not.',
    },
    {
      name: 'DetailSection',
      when:
        'The content is a record’s own long text. It is one field of one record, not an entry in a ' +
        'conversation, and nothing about it is grouped or timed.',
    },
  ],

  accessibility:
    'Each message is a `role="article"`, so a screen reader can move message by message instead of ' +
    'paragraph by paragraph through a whole conversation. The time is a `<time>` with a machine-readable ' +
    '`datetime`, so "9:14 AM" is not the only form of it available. The avatar is `aria-hidden`: it ' +
    'repeats the author’s name, and reading both says the same person twice before every message. The ' +
    'fold control carries `aria-expanded`. Left to the author: the AUTHOR name. A grouped message draws ' +
    'no name by design, so a reader moving article by article through a run of grouped messages hears ' +
    'the bodies without the name — which is correct, because the name did not change, and is the reason ' +
    'grouping must only be set when it genuinely did not.',

  examples: [
    {
      key: 'message',
      label: 'A message with its header',
      summary: 'The first message from somebody: avatar, name, time, body.',
      placement: {
        component: 'caos-message-bubble',
        inputs: {
          author: 'Ada Lovelace',
          at: '2026-09-13T09:14:00.000Z',
          body: 'Pushed the fix — it was **the index**, not the query.',
        },
      },
    },
    {
      key: 'grouped',
      label: 'The next message from the same person',
      summary:
        'Grouped: no avatar, no name, no time. The message above said all three a moment ago, and ' +
        'repeating them is what makes a conversation read as a log file.',
      placement: [
        {
          component: 'caos-message-bubble',
          inputs: {
            author: 'Ada Lovelace',
            at: '2026-09-13T09:14:00.000Z',
            body: 'Pushed the fix — it was **the index**, not the query.',
          },
        },
        {
          component: 'caos-message-bubble',
          inputs: {
            author: 'Ada Lovelace',
            at: '2026-09-13T09:15:00.000Z',
            body: 'Plan reads back clean now.',
            grouped: true,
          },
        },
      ],
    },
    {
      key: 'markdown',
      label: 'A body with marks in it',
      summary: 'The small markdown a message may carry, drawn as nodes rather than as HTML.',
      placement: {
        component: 'caos-message-bubble',
        inputs: {
          author: 'Grace Hopper',
          at: '2026-09-13T10:02:00.000Z',
          body:
            'Three things to check:\n\n' +
            '- the `at` bound is inclusive\n' +
            '- the filter reaches the index\n' +
            '- an empty page says *why*\n\n' +
            'See https://example.test/notes for the rest.',
        },
      },
    },
  ],
};

export default docs;
